import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot, limit, getDocs, getDoc, setDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ORDER_STATUS, cancelExpiredOrders } from '../../firebase/orders';
import './AdminNotificationsModal.css';

const LOW_STOCK_THRESHOLD = 10;

// Firestore doc for shared read tracking (synced across all admin devices)
const READ_IDS_DOC  = doc(db, 'settings', 'adminNotifReadIds');
// Firestore doc for dismissed expired notifs
const DISMISSED_DOC = doc(db, 'settings', 'adminNotifDismissed');

// ─── Notification builder ─────────────────────────────────────────────────────
function buildNotifications(orders, chats, lowStockProducts, expiredOrders = []) {
    const notifs = [];

    orders.forEach(order => {
        const status = order.status;

        if (status === ORDER_STATUS.PENDING_APPROVAL || status === 'pending') {
            notifs.push({
                id:        `order-pending-${order.firestoreId || order.orderId}`,
                type:      'new-order',
                icon:      '📋',
                iconBg:    '#fff8e1',
                iconColor: '#b07c00',
                title:     'New Order Submitted',
                message:   `${order.customerName || 'A customer'} submitted an order for approval.`,
                orderId:   order.orderId,
                time:      order.createdAt,
                action:    { label: 'Review Order →', path: '/admin/orders/details', state: { orderData: order } },
            });
        }

        if (status === ORDER_STATUS.AWAITING_PAYMENT) {
            notifs.push({
                id:        `order-gcash-${order.firestoreId || order.orderId}`,
                type:      'gcash',
                icon:      '💳',
                iconBg:    '#f3e5f5',
                iconColor: '#7b1fa2',
                title:     'GCash Payment Uploaded',
                message:   `${order.customerName || 'A customer'} is awaiting payment confirmation.`,
                orderId:   order.orderId,
                time:      order.updatedAt || order.createdAt,
                action:    { label: 'Confirm Payment →', path: '/admin/orders/details', state: { orderData: order } },
            });
        }

        if (status === ORDER_STATUS.IN_PRODUCTION && order.paymentMethod === 'Cash on Delivery') {
            notifs.push({
                id:        `order-cod-${order.firestoreId || order.orderId}`,
                type:      'cod',
                icon:      '⚙️',
                iconBg:    '#e6f4ea',
                iconColor: '#2d7a3a',
                title:     'COD Order In Production',
                message:   `Order by ${order.customerName || 'a customer'} is now in production (COD).`,
                orderId:   order.orderId,
                time:      order.updatedAt || order.createdAt,
                action:    { label: 'View Order →', path: '/admin/orders/details', state: { orderData: order } },
            });
        }
    });

    expiredOrders.forEach(order => {
        notifs.push({
            id:        `order-expired-${order.firestoreId || order.orderId}`,
            type:      'expired',
            icon:      '❌',
            iconBg:    '#fdecea',
            iconColor: '#c62828',
            title:     'Order Auto-Cancelled',
            message:   `${order.customerName || 'A customer'}'s order was cancelled — did not check out within 72 hours.`,
            orderId:   order.orderId,
            time:      order.updatedAt,
            action:    { label: 'View Order →', path: '/admin/orders/details', state: { orderData: order } },
        });
    });

    lowStockProducts.forEach(product => {
        const isOut = (product.stockQuantity ?? 0) === 0;
        notifs.push({
            id:        `stock-${product.id}`,
            type:      isOut ? 'out-of-stock' : 'low-stock',
            icon:      isOut ? '⚠️' : '📉',
            iconBg:    isOut ? '#fdecea' : '#fff8e1',
            iconColor: isOut ? '#c62828' : '#b07c00',
            title:     isOut ? 'Out of Stock' : 'Low Stock Alert',
            message:   `${product.name} has ${isOut ? 'no stock remaining' : `only ${product.stockQuantity} units left`}.`,
            orderId:   null,
            time:      product.updatedAt || null,
            action:    { label: 'Manage Inventory →', path: '/admin/products/inventory', state: null },
        });
    });

    chats.forEach(chat => {
        if ((chat.unreadByAdmin || 0) > 0) {
            notifs.push({
                id:        `chat-${chat.id}`,
                type:      'chat',
                icon:      '💬',
                iconBg:    '#e3f0ff',
                iconColor: '#1a5faa',
                title:     'New Chat Message',
                message:   `${chat.userName || 'A customer'} sent ${chat.unreadByAdmin} new message${chat.unreadByAdmin > 1 ? 's' : ''}.`,
                orderId:   null,
                time:      chat.lastMessageTime,
                action:    { label: 'Reply →', path: '/admin/chats', state: { openChatUserId: chat.userId || chat.id } },
            });
        }
    });

    notifs.sort((a, b) => {
        const ta = a.time?.seconds ? a.time.seconds * 1000 : (a.time ? new Date(a.time).getTime() : 0);
        const tb = b.time?.seconds ? b.time.seconds * 1000 : (b.time ? new Date(b.time).getTime() : 0);
        return tb - ta;
    });

    return notifs;
}

function formatTime(value) {
    if (!value) return '';
    const d = value?.toDate ? value.toDate() : new Date(value?.seconds ? value.seconds * 1000 : value);
    if (isNaN(d.getTime())) return '';
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7)  return `${days}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function AdminNotificationsModal({ isOpen, onClose }) {
    const navigate = useNavigate();
    const [orders,           setOrders]           = useState([]);
    const [chats,            setChats]            = useState([]);
    const [lowStockProducts, setLowStockProducts] = useState([]);
    const [expiredOrders,    setExpiredOrders]    = useState([]);

    const [readIds,      setReadIds]      = useState(new Set());
    const [dismissedIds, setDismissedIds] = useState(new Set());

    // ── FIX: Load dismissed + read IDs from Firestore BEFORE subscriptions fire
    // so there are no ghost notifications on first render
    const [metaLoaded, setMetaLoaded] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setMetaLoaded(false);

        Promise.all([
            getDoc(READ_IDS_DOC).catch(() => null),
            getDoc(DISMISSED_DOC).catch(() => null),
        ]).then(([readSnap, dismissedSnap]) => {
            if (readSnap?.exists())      setReadIds(new Set(readSnap.data().ids || []));
            if (dismissedSnap?.exists()) setDismissedIds(new Set(dismissedSnap.data().ids || []));
            setMetaLoaded(true);
        });
    }, [isOpen]);

    const markOneRead = useCallback((id) => {
        setReadIds(prev => {
            const updated = new Set([...prev, id]);
            setDoc(READ_IDS_DOC, { ids: [...updated] }, { merge: true })
                .catch(err => console.warn('Failed to save readId:', err));
            return updated;
        });
    }, []);

        // ── Mark all as read (restored) ───────────────────────────────────────────
        const markAllRead = useCallback((notifications) => {
            // Exclude chat notifications — their read state is managed by unreadByAdmin
            const allIds = new Set(notifications.filter(n => n.type !== 'chat').map(n => n.id));
            setDoc(READ_IDS_DOC, { ids: [...allIds] }, { merge: true })
            .catch(err => console.warn('Failed to mark all read:', err));
            setReadIds(allIds);
        }, []);

        // ── Remove all notifications ──────────────────────────────────────────────
        const handleRemoveAll = useCallback((notifications) => {
            const allIds = notifications
            .filter(n => n.type !== 'chat')
            .map(n => n.id);
            
        const allReadIds = new Set(allIds);
        setDoc(READ_IDS_DOC, { ids: [...allReadIds] }, { merge: true })
            .catch(err => console.warn('Failed to save readIds:', err));
        setReadIds(allReadIds);
            
        setDismissedIds(prev => {
            const updated = new Set([...prev, ...allIds]);
            setDoc(DISMISSED_DOC, { ids: [...updated] }, { merge: true })
                .catch(err => console.warn('Failed to save dismissedIds:', err));
            return updated;
        });
    }, []);

    // ── Firestore subscriptions ───────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen) return;

        cancelExpiredOrders().catch(err => console.warn('cancelExpiredOrders error:', err));

        const ordersQuery = query(
            collection(db, 'orders'),
            where('status', 'in', [
                ORDER_STATUS.PENDING_APPROVAL,
                ORDER_STATUS.APPROVED,
                ORDER_STATUS.AWAITING_PAYMENT,
                'pending',
            ]),
            limit(50)
        );

        const unsubOrders = onSnapshot(ordersQuery, snap => {
            setOrders(snap.docs.map(d => ({ firestoreId: d.id, ...d.data() })));
        }, err => console.warn('AdminNotifications orders error:', err.message));

        const codQuery = query(
            collection(db, 'orders'),
            where('status', '==', ORDER_STATUS.IN_PRODUCTION),
            where('paymentMethod', '==', 'Cash on Delivery'),
            orderBy('createdAt', 'desc'),
            limit(20)
        );

        const unsubCod = onSnapshot(codQuery, snap => {
            setOrders(prev => {
                const existingIds = new Set(prev.map(o => o.firestoreId));
                const codDocs = snap.docs
                    .map(d => ({ firestoreId: d.id, ...d.data() }))
                    .filter(o => !existingIds.has(o.firestoreId));
                return [...prev, ...codDocs];
            });
        }, err => console.warn('AdminNotifications COD error:', err.message));

        const chatsQuery = query(
            collection(db, 'chats'),
            where('unreadByAdmin', '>', 0)
        );

        const unsubChats = onSnapshot(chatsQuery, snap => {
            setChats(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }, err => console.warn('AdminNotifications chats error:', err.message));

        const expiredQuery = query(
            collection(db, 'orders'),
            where('status', '==', ORDER_STATUS.CANCELLED),
            where('cancellationReason', '==', 'Approval window expired — customer did not check out within 72 hours.'),
            orderBy('updatedAt', 'desc'),
            limit(10)
        );

        const unsubExpired = onSnapshot(expiredQuery, snap => {
            const recent = snap.docs
                .map(d => ({ firestoreId: d.id, ...d.data() }))
                .filter(o => {
                    if (!o.updatedAt) return false;
                    const t = o.updatedAt?.seconds
                        ? o.updatedAt.seconds * 1000
                        : new Date(o.updatedAt).getTime();
                    return t > Date.now() - 7 * 24 * 60 * 60 * 1000;
                });
            setExpiredOrders(recent);
        }, err => console.warn('AdminNotifications expired error:', err.message));

        getDocs(collection(db, 'products'))
            .then(snap => {
                const low = snap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(p => (p.stockQuantity ?? 0) <= LOW_STOCK_THRESHOLD);
                setLowStockProducts(low);
            })
            .catch(err => console.warn('AdminNotifications stock error:', err.message));

        return () => {
            unsubOrders();
            unsubCod();
            unsubChats();
            unsubExpired();
        };
    }, [isOpen]);

    const allNotifications = buildNotifications(orders, chats, lowStockProducts, expiredOrders);

    // ── FIX: Don't render any notifications until dismissed/read IDs are loaded
    // This prevents ghost notifications flashing on open/reload
    const notifications = metaLoaded
        ? allNotifications.filter(n => n.type === 'chat' || !dismissedIds.has(n.id))
        : [];

    const unreadCount = notifications.filter(n => n.type === 'chat' || !readIds.has(n.id)).length;

    const handleNotifClick = async (notif) => {
        if (notif.type !== 'chat') markOneRead(notif.id);
        if (notif.type === 'expired') {
            setDismissedIds(prev => {
                const updated = new Set([...prev, notif.id]);
                setDoc(DISMISSED_DOC, { ids: [...updated] }, { merge: true })
                    .catch(err => console.warn('Failed to save dismissedId:', err));
                return updated;
            });
        }
        navigate(notif.action.path, notif.action.state ? { state: notif.action.state } : undefined);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="admin-notif-backdrop" onClick={onClose}>
            <div className="admin-notif-modal-wrapper" onClick={e => e.stopPropagation()}>
                <div className="admin-notif-modal">

                    {/* Header */}
                    <div className="admin-notif-header">
                        <div className="admin-notif-header-left">
                            <h3 className="admin-notif-title">Notifications</h3>
                            {unreadCount > 0 && (
                                <span className="admin-notif-count-badge">{unreadCount} new</span>
                            )}
                        </div>
                        <div className="admin-notif-header-right">
                            {unreadCount > 0 && (
                                <button
                                    className="admin-notif-mark-all-btn"
                                    onClick={() => markAllRead(notifications)}
                                >
                                    Mark all as read
                                </button>
                            )}
                            {unreadCount === 0 && notifications.length > 0 && (
                                <button
                                    className="admin-notif-clear-read-btn"
                                    onClick={() => handleRemoveAll(notifications)}
                                >
                                    Clear all read
                                </button>
                            )}
                            <button
                                className="admin-notif-close-btn"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="admin-notif-body">
                        {!metaLoaded ? (
                            <div className="admin-notif-loading">
                                <div className="admin-notif-spinner" />
                                <span>Loading notifications…</span>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="admin-notif-empty">
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1.5">
                                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                                </svg>
                                <p>No new notifications</p>
                                <span>All orders, stock, and messages are up to date.</span>
                            </div>
                        ) : (
                            <div className="admin-notif-list">
                                {notifications.map(notif => {
                                    const isUnread = !readIds.has(notif.id);
                                    return (
                                        <div
                                            key={notif.id}
                                            className={`admin-notif-item${isUnread ? ' unread' : ''}`}
                                            onClick={() => handleNotifClick(notif)}
                                        >
                                            {isUnread && <div className="admin-notif-unread-dot" />}

                                            <div
                                                className="admin-notif-icon"
                                                style={{ background: notif.iconBg, color: notif.iconColor }}
                                            >
                                                {notif.icon}
                                            </div>

                                            <div className="admin-notif-content">
                                                <div className="admin-notif-item-header">
                                                    <strong className="admin-notif-item-title">{notif.title}</strong>
                                                    <span className="admin-notif-item-time">{formatTime(notif.time)}</span>
                                                </div>
                                                <p className="admin-notif-item-msg">{notif.message}</p>
                                                {notif.orderId && (
                                                    <span className="admin-notif-order-id">#{notif.orderId}</span>
                                                )}
                                                <span
                                                    className="admin-notif-action-label"
                                                    style={{ color: notif.iconColor }}
                                                >
                                                    {notif.action.label}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer removed — actions moved to header */}
                </div>
            </div>
        </div>
    );
}